// ============================================================
// SecurityInIoT.jsx
//
// Course content page for "Security in IoT" lesson. Rewritten to:
//   1) remove an entire dead component — a `SectionHeader` was
//      defined at the bottom of the original file but never called
//      anywhere; every section used its own inline header markup
//      instead,
//   2) remove three dead imports (FaUserShield, FaTerminal,
//      MdSecurity — none were ever rendered),
//   3) fix the TLS code example — it referenced `ROOT_CA_CERT`
//      without ever declaring it, never connected to Wi-Fi, and
//      had no loop() at all. Arduino requires both setup() and
//      loop() to exist or the sketch won't link, so this one
//      would not have compiled,
//   4) soften an imprecise claim that TLS keeps data "unreadable
//      without the private key" — modern TLS encrypts traffic with
//      a negotiated session key established during the handshake,
//      not by decrypting each packet with the long-term private key,
//   5) match the app-wide dark-purple/goldenrod look, use
//      arrow-function components, and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import SecurityIcon from "@mui/icons-material/Security";
import LockIcon from "@mui/icons-material/Lock";
import HubIcon from "@mui/icons-material/Hub";
import BugReportIcon from "@mui/icons-material/BugReport";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const THREATS = [
    { title: "Weak Auth", icon: <FingerprintIcon />, description: "Hardcoded credentials and default passwords let attackers gain easy administrative control via Telnet or SSH." },
    { title: "Man-in-the-Middle", icon: <HubIcon />, description: "Unencrypted MQTT or HTTP traffic can be intercepted and spoofed, letting attackers inject malicious commands." },
    { title: "Edge Vulnerabilities", icon: <SystemUpdateAltIcon />, description: "Outdated firmware without 'Secure Boot' allows unauthorized, malicious code to run directly on the device." },
];

const CHECKLIST = [
    "Unique device identifiers and certificates",
    "Encryption for data at rest (flash encryption)",
    "VLAN segmentation for IoT sub-networks",
    "Disabled JTAG/UART access in production",
    "Signed firmware binaries for OTA updates",
    "Rate limiting for API requests",
];

// Sub-components (arrow-function style, defined before first use)

const ThreatCard = ({ title, icon, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold }}>{icon}</Avatar>
            <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// A complete, compiling sketch — the original referenced ROOT_CA_CERT
// without declaring it, never connected to Wi-Fi, and had no loop()
// at all (Arduino requires both setup() and loop() to link).
const SECURE_MQTT_SKETCH = `#include <WiFi.h>
#include <WiFiClientSecure.h>
#include <PubSubClient.h>

// Paste your broker's root CA certificate here as a PEM string.
const char* ROOT_CA_CERT = "-----BEGIN CERTIFICATE-----\\n...\\n-----END CERTIFICATE-----";

WiFiClientSecure secureClient;
PubSubClient client(secureClient);

void setup() {
  WiFi.begin("SSID", "PASSWORD");
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }

  secureClient.setCACert(ROOT_CA_CERT);
  client.setServer("mqtt.secure-iot.com", 8883); // 8883 = standard MQTTS port

  if (client.connect("Hardened_Node_01")) {
    client.publish("telemetry/secure", "encrypted_payload");
  }
}

void loop() {
  client.loop();
}`;

// ── Main Page ───────────────────────────────────────────────────

const SecurityInIoT = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Cybersecurity · Module 08"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    IoT <span style={{ color: BRAND.teal }}>Security</span> Hardening
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Connected devices are a prime target for attackers. Learn to build "defense-in-depth"
                    strategies that protect hardware, firmware, and data streams.
                </Typography>
            </Box>

            {/* THE THREAT LANDSCAPE */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <BugReportIcon sx={{ color: BRAND.gold, fontSize: 24 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>The Threat Landscape</Typography>
                </Box>
                <Grid container spacing={3}>
                    {THREATS.map((t) => (
                        <Grid item xs={12} md={4} key={t.title}>
                            <ThreatCard {...t} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* SECURE IMPLEMENTATION */}
            <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                <Grid item xs={12} md={5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <VpnLockIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>TLS/SSL Encryption</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        Never transmit data in plaintext. With <strong>Transport Layer Security (TLS)</strong>,
                        a certificate-based handshake establishes a session key that encrypts everything that
                        follows — so an intercepted packet is unreadable without that key.
                    </Typography>
                    <Stack spacing={2}>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(218,165,32,0.06)", border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="subtitle2" sx={{ color: BRAND.gold, display: "flex", alignItems: "center", gap: 1 }}>
                                <VerifiedUserIcon fontSize="small" /> Root Certificates
                            </Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>Verify the identity of the cloud broker before sending data.</Typography>
                        </Box>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(45,212,191,0.06)", border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="subtitle2" sx={{ color: BRAND.teal }}>Port 8883</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>The industry-standard port for MQTTS (secure MQTT).</Typography>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                    <CodeTerminal label="SECURE_MQTT_TLS.ino" code={SECURE_MQTT_SKETCH} />
                </Grid>
            </Grid>

            {/* BEST PRACTICES CHECKLIST */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4 }}>Security Checklist</Typography>
                <Grid container spacing={2}>
                    {CHECKLIST.map((item) => (
                        <Grid item xs={12} md={4} key={item}>
                            <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: "rgba(255,255,255,0.03)" }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <SecurityIcon sx={{ color: BRAND.gold }} />
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{item}</Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <LockIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Mission Critical Security?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Don't let your device be the weak link. Audit your code and secure your keys.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Launch Security Audit Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="OWASP IoT Top 10" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default SecurityInIoT;
